/**
 * useWsSession — subscribe to a WS session for the lifetime of a component.
 *
 * Usage (typically in the Play page):
 *
 *   const { status } = useWsSession(sessionId);
 *
 * On mount: calls `bridge.subscribe(sessionId)` so the server begins
 * streaming events for this session.
 * On unmount (or sessionId change): calls `bridge.unsubscribe(prevSessionId)`.
 *
 * Graceful degradation:
 *   - If the WsBridgeProvider is not mounted (context value is undefined),
 *     no-ops and returns `{ status: 'idle' }`.
 *   - If the bridge is null (not yet authenticated), no-ops and returns
 *     `{ status: 'idle' }`.
 *
 * The existing REST fallback (POST /chat/message) continues to work
 * regardless — the WS subscription is additive streaming.
 */

import { useContext, useEffect, useRef } from 'react';
import type { WsStatus } from './socket';
import { WsBridgeContext } from './WsBridgeProvider';

interface UseWsSessionResult {
  status: WsStatus;
}

export function useWsSession(sessionId: string | undefined): UseWsSessionResult {
  // Use the raw context directly — returns undefined if provider is not
  // mounted, which we handle gracefully instead of throwing.
  const ctx = useContext(WsBridgeContext);
  const bridge = ctx?.bridge ?? null;
  const status: WsStatus = ctx?.status ?? 'idle';

  const prevSessionIdRef = useRef<string | undefined>(undefined);

  useEffect(() => {
    // If the bridge is not available, no-op gracefully.
    if (!bridge) return;

    const prevSessionId = prevSessionIdRef.current;

    // Unsubscribe from the previous session if it changed.
    if (prevSessionId && prevSessionId !== sessionId) {
      bridge.unsubscribe(prevSessionId);
    }

    // Open the bridge (idempotent) and subscribe to the new session.
    if (sessionId) {
      bridge.open();
      bridge.subscribe(sessionId);
      prevSessionIdRef.current = sessionId;
    }

    // Cleanup: unsubscribe on unmount.
    return () => {
      if (sessionId) {
        bridge.unsubscribe(sessionId);
      }
      prevSessionIdRef.current = undefined;
    };
  }, [bridge, sessionId]);

  return { status };
}
